import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, To } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import './SalvPage.scss';
import { SalvProgressCircle, SalvTooltipWrapper } from '@salvapp/ui-components';

interface Props {
  title: ReactNode;
  children?: ReactNode;
  subTitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: { title: string; to: To } | Array<{ title: string; to: To }>;
  className?: string;
  style?: CSSProperties;
  // you won't get the benefits of type narrowing
  isLoading?: boolean;
}

const SalvPage = ({
  title,
  children,
  subTitle,
  actions,
  breadcrumb,
  className = '',
  isLoading = false,
  style,
}: Props): React.JSX.Element => {
  const breadcrumbs = breadcrumb ? (Array.isArray(breadcrumb) ? breadcrumb : [breadcrumb]) : [];
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [isTitleOverflown, setTitleIsOverflown] = useState(false);
  useEffect(() => {
    const element = titleRef.current!;
    setTitleIsOverflown(element.scrollWidth > element.clientWidth);
  }, [title]);

  return (
    <div className={`salv-page ${className}`} style={style}>
      <div className={'salv-header'}>
        <div className={'salv-header-main'}>
          {breadcrumbs.length > 0 && (
            <span className={'salv-breadcrumbs'}>
              {breadcrumbs.map((crumb) => (
                <React.Fragment key={crumb.title}>
                  <Link to={crumb.to}>{crumb.title}</Link>
                  <FontAwesomeIcon icon={faChevronRight} />
                </React.Fragment>
              ))}
            </span>
          )}
          <div className={'salv-title'}>
            <SalvTooltipWrapper tooltip={title} isDisabled={!isTitleOverflown}>
              <h1 ref={titleRef}>{title}</h1>
            </SalvTooltipWrapper>
          </div>

          <div className={'salv-actions'}>{actions}</div>
        </div>
        {subTitle && <div className={'salv-header-secondary'}>{subTitle}</div>}
      </div>
      {!isLoading ? (
        <div className={'salv-content'}>{children}</div>
      ) : (
        <div className='p-a-16'>
          <SalvProgressCircle />
        </div>
      )}
    </div>
  );
};

export default SalvPage;
